import React, { useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '../utils/supabase';
import { Patient, Doctor, Appointment, Service, Insurance, Specialty, Contact, Company, Opportunity, MedicalHistory, MedicalEntry, Interaction, ClinicalHistory, Role, Permission, AuditLog, AuditAction, FollowUpNote, ContactStatus, ContactSource, normalizeRoleName } from '../types';
import { getToday, getDayOfWeekKey } from '../utils/helpers';
import { formatCurrency } from '../utils/helpers';
import { getInitialData } from '../utils/initialData';
import { useAuth } from './AuthContext';
interface DataContextType {
  patients: Patient[];
  addPatient: (patient: Omit<Patient, 'id' | 'medicalHistory' | 'interactions' | 'createdAt'>) => Promise<Patient>;
  updatePatient: (id: string, data: Partial<Patient>) => Promise<void>;
  deletePatient: (id: string) => Promise<void>;
  getPatient: (id: string) => Patient | undefined;
  updateMedicalHistory: (patientId: string, history: Partial<MedicalHistory>) => Promise<void>;
  addMedicalEntry: (patientId: string, entry: Omit<MedicalEntry, 'id' | 'date'>) => Promise<void>;
  addPatientInteraction: (patientId: string, interaction: Omit<Interaction, 'id' | 'date'>) => Promise<void>;
  doctors: Doctor[];
  addDoctor: (doctor: Omit<Doctor, 'id' | 'createdAt'>) => Promise<Doctor>;
  updateDoctor: (id: string, data: Partial<Doctor>) => Promise<void>;
  deleteDoctor: (id: string) => Promise<void>;
  getDoctor: (id: string) => Doctor | undefined;
  getDoctorsBySpecialty: (specialtyId: string) => Doctor[];
  appointments: Appointment[];
  addAppointment: (appointment: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Appointment>;
  updateAppointment: (id: string, data: Partial<Appointment>) => Promise<void>;
  cancelAppointment: (id: string, reason: string) => Promise<void>;
  rescheduleAppointment: (id: string, newDate: string, newTime: string) => Promise<void>;
  completeAppointment: (id: string, nextAppointmentDate?: string, nextAppointmentNotes?: string) => Promise<void>;
  getAppointment: (id: string) => Appointment | undefined;
  getAppointmentsByDate: (date: string) => Appointment[];
  getAppointmentsByPatient: (patientId: string) => Appointment[];
  getAppointmentsByDoctor: (doctorId: string) => Appointment[];
  checkDoctorAvailability: (doctorId: string, date: string, time: string, excludeId?: string) => boolean;
  syncAllAppointmentsToSheets: () => Promise<void>;
  services: Service[];
  addService: (service: Omit<Service, 'id'>) => Promise<Service>;
  updateService: (id: string, data: Partial<Service>) => Promise<void>;
  deleteService: (id: string) => Promise<void>;
  getService: (id: string) => Service | undefined;
  insurances: Insurance[];
  addInsurance: (insurance: Omit<Insurance, 'id'>) => Promise<Insurance>;
  updateInsurance: (id: string, data: Partial<Insurance>) => Promise<void>;
  deleteInsurance: (id: string) => Promise<void>;
  getInsurance: (id: string) => Insurance | undefined;
  specialties: Specialty[];
  addSpecialty: (specialty: Omit<Specialty, 'id'>) => Promise<Specialty>;
  updateSpecialty: (id: string, data: Partial<Specialty>) => Promise<void>;
  deleteSpecialty: (id: string) => Promise<void>;
  getSpecialty: (id: string) => Specialty | undefined;
  contacts: Contact[];
  addContact: (contact: Omit<Contact, 'id' | 'interactions' | 'createdAt'>) => Promise<Contact>;
  updateContact: (id: string, data: Partial<Contact>) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;
  getContact: (id: string) => Contact | undefined;
  addContactInteraction: (contactId: string, interaction: Omit<Interaction, 'id' | 'date'>) => Promise<void>;
  companies: Company[];
  addCompany: (company: Omit<Company, 'id' | 'createdAt'>) => Promise<Company>;
  updateCompany: (id: string, data: Partial<Company>) => Promise<void>;
  deleteCompany: (id: string) => Promise<void>;
  getCompany: (id: string) => Company | undefined;
  opportunities: Opportunity[];
  addOpportunity: (opportunity: Omit<Opportunity, 'id' | 'interactions' | 'createdAt' | 'updatedAt'>) => Promise<Opportunity>;
  updateOpportunity: (id: string, data: Partial<Opportunity>) => Promise<void>;
  deleteOpportunity: (id: string) => Promise<void>;
  getOpportunity: (id: string) => Opportunity | undefined;
  addOpportunityInteraction: (opportunityId: string, interaction: Omit<Interaction, 'id' | 'date'>) => Promise<void>;
  clinicalHistories: ClinicalHistory[];
  addClinicalHistory: (history: Omit<ClinicalHistory, 'id' | 'createdAt' | 'updatedAt'>) => Promise<ClinicalHistory>;
  updateClinicalHistory: (id: string, data: Partial<ClinicalHistory>) => Promise<void>;
  deleteClinicalHistory: (id: string) => Promise<void>;
  getClinicalHistory: (id: string) => ClinicalHistory | undefined;
  getClinicalHistoriesByPatient: (patientId: string) => ClinicalHistory[];
  roles: Role[];
  addRole: (role: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Role>;
  updateRole: (id: string, data: Partial<Role>) => Promise<void>;
  deleteRole: (id: string) => Promise<void>;
  getRole: (id: string) => Role | undefined;
  permissions: Permission[];
  getPermissionsByRole: (roleId: string) => Permission[];
  updatePermissions: (roleId: string, permissions: Permission[]) => Promise<void>;
  getTodayAppointmentsCount: () => number;
  getTotalOpportunitiesAmount: () => number;
  isLoading: boolean;
  isConnectedToSupabase: boolean;
  // Audit Log
  auditLogs: AuditLog[];
  addAuditLog: (log: Omit<AuditLog, 'id' | 'timestamp'>) => Promise<void>;
  clearAuditLogs: () => Promise<void>;
  // Follow-up Notes
  followUpNotes: FollowUpNote[];
  addFollowUpNote: (note: Omit<FollowUpNote, 'id' | 'createdAt'>) => Promise<FollowUpNote>;
  updateFollowUpNote: (id: string, data: Partial<FollowUpNote>) => Promise<void>;
  deleteFollowUpNote: (id: string) => Promise<void>;
}
const DataContext = createContext<DataContextType | undefined>(undefined);
export function DataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [insurances, setInsurances] = useState<Insurance[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [clinicalHistories, setClinicalHistories] = useState<ClinicalHistory[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [followUpNotes, setFollowUpNotes] = useState<FollowUpNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnectedToSupabase, setIsConnectedToSupabase] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  // Get current user for audit logging
  const {
    user
  } = useAuth();
  // ==================== ROW MAPPERS (Supabase → Frontend) ====================
  const mapPatient = (p: any): Patient => ({
    id: p.id,
    name: p.name,
    cedula: '',
    birthDate: '',
    age: 0,
    gender: p.gender,
    phone: p.phone,
    email: p.email,
    address: p.address,
    emergencyContact: p.emergency_contact || {
      name: '',
      relationship: '',
      phone: ''
    },
    insuranceId: p.insurance_id,
    medicalHistory: p.medical_history || {},
    interactions: p.interactions || [],
    createdAt: new Date(p.created_at)
  });
  const mapDoctor = (d: any): Doctor => ({
    id: d.id,
    name: d.name,
    gender: d.gender || 'masculino',
    specialtyId: d.specialty_id,
    phone: d.phone,
    email: d.email,
    schedule: d.schedule || {},
    createdAt: new Date(d.created_at)
  });
  const mapAppointment = (a: any): Appointment => ({
    id: a.id,
    patientId: a.patient_id,
    doctorId: a.doctor_id,
    serviceId: a.service_id,
    serviceIds: a.service_ids || [],
    date: a.date,
    time: a.time,
    duration: a.duration,
    status: a.status,
    notes: a.notes || '',
    createdByUserId: a.created_by_user_id || '',
    createdByUserName: a.created_by_user_name || '',
    nextAppointmentDate: a.next_appointment_date,
    nextAppointmentNotes: a.next_appointment_notes,
    cancelReason: a.cancel_reason,
    originalDate: a.original_date,
    originalTime: a.original_time,
    createdAt: new Date(a.created_at),
    updatedAt: new Date(a.updated_at)
  });
  const mapService = (s: any): Service => ({
    id: s.id,
    name: s.name,
    sku: s.sku || '',
    duration: Math.round(Number(s.duration)),
    price: Math.round(Number(s.price)),
    description: s.description || '',
    doctorIds: s.doctor_ids || []
  });
  const mapInsurance = (i: any): Insurance => ({
    id: i.id,
    name: i.name,
    company: i.company,
    coverageType: i.coverage_type,
    phone: i.phone,
    email: i.email
  });
  const mapContact = (c: any): Contact => ({
    id: c.id,
    name: c.name,
    phone: c.phone,
    email: c.email,
    company: '',
    position: '',
    contactStatus: (c.contact_status || 'nuevo') as ContactStatus,
    contactSource: (c.contact_source || 'otro') as ContactSource,
    notes: c.notes || '',
    interactions: c.interactions || [],
    createdAt: new Date(c.created_at),
    createdBy: c.created_by || ''
  });
  const mapCompany = (c: any): Company => ({
    id: c.id,
    name: c.name,
    industry: c.industry,
    size: c.size,
    location: c.location,
    phone: c.phone,
    email: c.email,
    website: c.website,
    notes: c.notes || '',
    createdAt: new Date(c.created_at)
  });
  const mapOpportunity = (o: any): Opportunity => ({
    id: o.id,
    name: o.name,
    contactId: o.contact_id,
    contactSource: o.contact_source || '',
    responsibleUserId: o.responsible_user_id || null,
    opportunityType: o.opportunity_type || '',
    stage: o.stage,
    amount: o.amount,
    probability: o.probability,
    notes: o.notes || '',
    interactions: o.interactions || [],
    createdAt: new Date(o.created_at),
    updatedAt: new Date(o.updated_at)
  });
  const mapClinicalHistory = (ch: any): ClinicalHistory => ({
    id: ch.id,
    patientId: ch.patient_id,
    date: new Date(ch.date + 'T12:00:00'),
    doctorId: ch.doctor_id,
    doctorName: ch.doctor_name,
    personalBackground: ch.personal_background || {},
    clinicalBackground: ch.clinical_background || {},
    physicalExam: {
      bloodPressure: ch.blood_pressure || '',
      bloodSugar: ch.blood_sugar || '',
      heartRate: ch.heart_rate || '',
      lungs: ch.lungs || '',
      eyes: ch.eyes || '',
      throat: ch.throat || '',
      abdomen: ch.abdomen || '',
      weight: ch.weight || '',
      height: ch.height || '',
      temperature: ch.temperature || '',
      positiveAspects: ch.positive_aspects || '',
      respiratoryRate: ch.respiratory_rate || '',
      weightLb: ch.weight_lb || ''
    } as any,
    diseaseHistory: {
      sinceWhen: ch.disease_history_since_when || '',
      previousTreatment: ch.disease_history_previous_treatment || '',
      evolution: ch.disease_history_evolution || ''
    },
    systemsReview: {
      general: ch.systems_review_general || '',
      cardioRespiratory: ch.systems_review_cardio_respiratory || '',
      gastrointestinal: ch.systems_review_gastrointestinal || '',
      genitourinary: ch.systems_review_genitourinary || '',
      osteoMuscular: ch.systems_review_osteo_muscular || '',
      neurological: ch.systems_review_neurological || '',
      skinAndAppendages: ch.systems_review_skin_appendages || ''
    },
    diagnosis: ch.diagnosis,
    treatment: ch.treatment,
    medicalIndications: ch.medical_indications || '',
    sessions: ch.sessions || '',
    sessionNumber: '',
    sessionServices: ch.session_services || [],
    comments: ch.comments || '',
    doctorSignature: ch.doctor_signature || '',
    attachedImages: ch.attached_images || [],
    createdAt: new Date(ch.created_at),
    updatedAt: new Date(ch.updated_at)
  });
  const mapRole = (r: any): Role => ({
    id: r.id,
    name: r.name,
    displayName: r.display_name,
    description: r.description || '',
    isSystemRole: r.is_system_role,
    createdAt: new Date(r.created_at),
    updatedAt: new Date(r.updated_at)
  });
  const mapPermission = (p: any): Permission => ({
    id: p.id,
    roleId: p.role_id,
    module: p.module,
    canCreate: p.can_create,
    canRead: p.can_read,
    canUpdate: p.can_update,
    canDelete: p.can_delete,
    permissionLevel: p.permission_level,
    createdAt: new Date(p.created_at),
    updatedAt: new Date(p.updated_at)
  });
  const mapAuditLog = (a: any): AuditLog => ({
    id: a.id || '',
    timestamp: new Date(a.created_at || Date.now()),
    userId: typeof a.user_id === 'string' ? a.user_id : String(a.user_id || ''),
    userName: typeof a.user_name === 'string' ? a.user_name : String(a.user_name || ''),
    action: typeof a.action === 'string' ? a.action : String(a.action || ''),
    module: typeof a.module === 'string' ? a.module : String(a.module || ''),
    entityId: typeof a.entity_id === 'string' ? a.entity_id : String(a.entity_id || ''),
    entityName: typeof a.entity_name === 'string' ? a.entity_name : String(a.entity_name || ''),
    details: typeof a.details === 'string' ? a.details : String(a.details || ''),
    changes: a.changes
  });
  const mapFollowUpNote = (n: any): FollowUpNote => ({
    id: n.id || '',
    userId: typeof n.user_id === 'string' ? n.user_id : String(n.user_id || ''),
    userName: typeof n.user_name === 'string' ? n.user_name : String(n.user_name || ''),
    contactName: typeof n.contact_name === 'string' ? n.contact_name : String(n.contact_name || ''),
    subject: typeof n.subject === 'string' ? n.subject : String(n.subject || ''),
    description: typeof n.description === 'string' ? n.description : String(n.description || ''),
    createdAt: new Date(n.created_at || Date.now())
  });
  // ==================== SUPABASE REALTIME SUBSCRIPTIONS ====================
  useEffect(() => {
    if (!isConnectedToSupabase || !dataLoaded) return;
    console.log('🔴 Configurando Supabase Realtime...');
    const channel = supabase.channel('savidna-realtime')
    // PATIENTS
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'patients'
    }, payload => {
      console.log('🔴 RT: Patient INSERT', payload.new.id);
      setPatients(prev => {
        if (prev.some(p => p.id === payload.new.id)) return prev;
        return [...prev, mapPatient(payload.new)];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'patients'
    }, payload => {
      console.log('🔴 RT: Patient UPDATE', payload.new.id);
      setPatients(prev => prev.map(p => p.id === payload.new.id ? mapPatient(payload.new) : p));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'patients'
    }, payload => {
      console.log('🔴 RT: Patient DELETE', payload.old.id);
      setPatients(prev => prev.filter(p => p.id !== payload.old.id));
    })
    // DOCTORS
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'doctors'
    }, payload => {
      console.log('🔴 RT: Doctor INSERT', payload.new.id);
      setDoctors(prev => {
        if (prev.some(d => d.id === payload.new.id)) return prev;
        return [...prev, mapDoctor(payload.new)];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'doctors'
    }, payload => {
      setDoctors(prev => prev.map(d => d.id === payload.new.id ? mapDoctor(payload.new) : d));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'doctors'
    }, payload => {
      setDoctors(prev => prev.filter(d => d.id !== payload.old.id));
    })
    // APPOINTMENTS
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'appointments'
    }, payload => {
      console.log('🔴 RT: Appointment INSERT', payload.new.id);
      setAppointments(prev => {
        if (prev.some(a => a.id === payload.new.id)) return prev;
        return [...prev, mapAppointment(payload.new)];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'appointments'
    }, payload => {
      console.log('🔴 RT: Appointment UPDATE', payload.new.id);
      setAppointments(prev => prev.map(a => a.id === payload.new.id ? mapAppointment(payload.new) : a));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'appointments'
    }, payload => {
      console.log('🔴 RT: Appointment DELETE', payload.old.id);
      setAppointments(prev => prev.filter(a => a.id !== payload.old.id));
    })
    // SERVICES
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'services'
    }, payload => {
      setServices(prev => {
        if (prev.some(s => s.id === payload.new.id)) return prev;
        return [...prev, mapService(payload.new)];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'services'
    }, payload => {
      setServices(prev => prev.map(s => s.id === payload.new.id ? mapService(payload.new) : s));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'services'
    }, payload => {
      setServices(prev => prev.filter(s => s.id !== payload.old.id));
    })
    // INSURANCES
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'insurances'
    }, payload => {
      setInsurances(prev => {
        if (prev.some(i => i.id === payload.new.id)) return prev;
        return [...prev, mapInsurance(payload.new)];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'insurances'
    }, payload => {
      setInsurances(prev => prev.map(i => i.id === payload.new.id ? mapInsurance(payload.new) : i));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'insurances'
    }, payload => {
      setInsurances(prev => prev.filter(i => i.id !== payload.old.id));
    })
    // SPECIALTIES
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'specialties'
    }, payload => {
      setSpecialties(prev => {
        if (prev.some(s => s.id === payload.new.id)) return prev;
        return [...prev, payload.new as Specialty];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'specialties'
    }, payload => {
      setSpecialties(prev => prev.map(s => s.id === payload.new.id ? payload.new as Specialty : s));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'specialties'
    }, payload => {
      setSpecialties(prev => prev.filter(s => s.id !== payload.old.id));
    })
    // CONTACTS
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'contacts'
    }, payload => {
      setContacts(prev => {
        if (prev.some(c => c.id === payload.new.id)) return prev;
        return [...prev, mapContact(payload.new)];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'contacts'
    }, payload => {
      setContacts(prev => prev.map(c => c.id === payload.new.id ? mapContact(payload.new) : c));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'contacts'
    }, payload => {
      setContacts(prev => prev.filter(c => c.id !== payload.old.id));
    })
    // COMPANIES
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'companies'
    }, payload => {
      setCompanies(prev => {
        if (prev.some(c => c.id === payload.new.id)) return prev;
        return [...prev, mapCompany(payload.new)];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'companies'
    }, payload => {
      setCompanies(prev => prev.map(c => c.id === payload.new.id ? mapCompany(payload.new) : c));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'companies'
    }, payload => {
      setCompanies(prev => prev.filter(c => c.id !== payload.old.id));
    })
    // OPPORTUNITIES
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'opportunities'
    }, payload => {
      setOpportunities(prev => {
        if (prev.some(o => o.id === payload.new.id)) return prev;
        return [...prev, mapOpportunity(payload.new)];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'opportunities'
    }, payload => {
      setOpportunities(prev => prev.map(o => o.id === payload.new.id ? mapOpportunity(payload.new) : o));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'opportunities'
    }, payload => {
      setOpportunities(prev => prev.filter(o => o.id !== payload.old.id));
    })
    // CLINICAL HISTORIES
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'clinical_histories'
    }, payload => {
      setClinicalHistories(prev => {
        if (prev.some(ch => ch.id === payload.new.id)) return prev;
        return [...prev, mapClinicalHistory(payload.new)];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'clinical_histories'
    }, payload => {
      setClinicalHistories(prev => prev.map(ch => ch.id === payload.new.id ? mapClinicalHistory(payload.new) : ch));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'clinical_histories'
    }, payload => {
      setClinicalHistories(prev => prev.filter(ch => ch.id !== payload.old.id));
    })
    // ROLES
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'roles'
    }, payload => {
      setRoles(prev => {
        if (prev.some(r => r.id === payload.new.id)) return prev;
        return [...prev, mapRole(payload.new)];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'roles'
    }, payload => {
      setRoles(prev => prev.map(r => r.id === payload.new.id ? mapRole(payload.new) : r));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'roles'
    }, payload => {
      setRoles(prev => prev.filter(r => r.id !== payload.old.id));
    })
    // PERMISSIONS
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'permissions'
    }, async () => {
      // For permissions, reload all to re-sync localStorage mapping
      console.log('🔴 RT: Permissions changed, reloading...');
      try {
        const [permsResult, rolesResult] = await Promise.all([supabase.from('permissions').select('*'), supabase.from('roles').select('*')]);
        if (permsResult.data && rolesResult.data) {
          setPermissions(permsResult.data.map(mapPermission));
          // Re-sync to localStorage
          const allPermsMap: Record<string, Record<string, string>> = {};
          for (const perm of permsResult.data) {
            const role = rolesResult.data.find((r: any) => r.id === perm.role_id);
            if (role) {
              if (!allPermsMap[perm.module]) allPermsMap[perm.module] = {};
              allPermsMap[perm.module][normalizeRoleName(role.name)] = perm.permission_level;
            }
          }
          localStorage.setItem('savidna-role-permissions', JSON.stringify(allPermsMap));
          window.dispatchEvent(new Event('permissions-updated'));
        }
      } catch (err) {
        console.error('Error reloading permissions via RT:', err);
      }
    })
    // AUDIT LOGS
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'audit_logs'
    }, payload => {
      setAuditLogs(prev => {
        if (prev.some(a => a.id === payload.new.id)) return prev;
        return [mapAuditLog(payload.new), ...prev];
      });
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'audit_logs'
    }, payload => {
      setAuditLogs(prev => prev.filter(a => a.id !== payload.old.id));
    })
    // FOLLOW-UP NOTES
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'follow_up_notes'
    }, payload => {
      setFollowUpNotes(prev => {
        if (prev.some(n => n.id === payload.new.id)) return prev;
        return [mapFollowUpNote(payload.new), ...prev];
      });
    }).on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'follow_up_notes'
    }, payload => {
      setFollowUpNotes(prev => prev.map(n => n.id === payload.new.id ? mapFollowUpNote(payload.new) : n));
    }).on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'follow_up_notes'
    }, payload => {
      setFollowUpNotes(prev => prev.filter(n => n.id !== payload.old.id));
    }).subscribe(status => {
      console.log(`🔴 Supabase Realtime status: ${status}`);
      if (status === 'SUBSCRIBED') {
        console.log('✅ Realtime activo - Los cambios se sincronizarán automáticamente');
      }
    });
    return () => {
      console.log('🔴 Desconectando Supabase Realtime...');
      supabase.removeChannel(channel);
    };
  }, [isConnectedToSupabase, dataLoaded]);
  // Helper to fetch ALL rows from a Supabase table (bypasses the 1000-row default limit)
  const fetchAllRows = async (table: string, orderBy?: {
    column: string;
    ascending: boolean;
  }) => {
    const PAGE_SIZE = 1000;
    let allData: any[] = [];
    let from = 0;
    let hasMore = true;
    while (hasMore) {
      let query = supabase.from(table).select('*').range(from, from + PAGE_SIZE - 1);
      if (orderBy) {
        query = query.order(orderBy.column, {
          ascending: orderBy.ascending
        });
      }
      const {
        data,
        error
      } = await query;
      if (error) {
        console.error(`❌ Error fetching ${table} (offset ${from}):`, error);
        throw error;
      }
      if (data && data.length > 0) {
        allData = allData.concat(data);
        from += PAGE_SIZE;
        hasMore = data.length === PAGE_SIZE;
      } else {
        hasMore = false;
      }
    }
    console.log(`📦 ${table}: ${allData.length} registros cargados`);
    return allData;
  };
  // Helper to add audit log entry
  const addAuditLog = async (log: Omit<AuditLog, 'id' | 'timestamp'>) => {
    const newLog: AuditLog = {
      ...log,
      id: crypto.randomUUID(),
      timestamp: new Date()
    };
    setAuditLogs(prev => [newLog, ...prev]);
    // Always try to persist to Supabase (don't rely on isConnectedToSupabase flag)
    try {
      const {
        data,
        error,
        status
      } = await supabase.from('audit_logs').insert({
        id: newLog.id,
        user_id: log.userId || 'system',
        user_name: log.userName || 'Sistema',
        action: log.action,
        module: log.module,
        entity_id: log.entityId || '',
        entity_name: log.entityName || '',
        details: log.details || '',
        changes: log.changes || null
      }).select();
      if (error) {
        console.error('❌ Error guardando audit log en Supabase:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          status
        });
      } else {
        console.log('✅ Audit log guardado en Supabase:', log.action, log.module, log.entityName);
      }
    } catch (err) {
      console.error('❌ Error de conexión guardando audit log:', err);
    }
  };
  const clearAuditLogs = async () => {
    setAuditLogs([]);
    localStorage.removeItem('savidna-audit-logs');
    // Always try to clear from Supabase
    try {
      const {
        error
      } = await supabase.from('audit_logs').delete().gte('created_at', '1970-01-01T00:00:00.000Z');
      if (error) {
        console.error('❌ Error limpiando audit logs en Supabase:', error);
      } else {
        console.log('✅ Audit logs limpiados en Supabase');
      }
    } catch (err) {
      console.error('❌ Error de conexión limpiando audit logs:', err);
    }
  };
  // Internal helper that uses current user
  const logAction = (action: AuditAction, module: string, entityId: string, entityName: string, details: string, changes?: {
    field: string;
    oldValue: string;
    newValue: string;
  }[]) => {
    // Fire and forget - don't await to avoid blocking the UI
    addAuditLog({
      userId: user?.id || 'system',
      userName: user?.name || 'Sistema',
      action,
      module,
      entityId,
      entityName,
      details,
      changes
    });
  };
  // Connect to Supabase directly (no auth session needed - RLS disabled)
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      try {
        // Test direct connection to Supabase
        const {
          error
        } = await supabase.from('specialties').select('id').limit(1);
        if (!error) {
          console.log('✅ Supabase conectado - Cargando datos...');
          setIsConnectedToSupabase(true);
          await loadFromSupabase();
        } else {
          console.log('⚠️ Error Supabase:', error.message, '- Usando localStorage');
          setIsConnectedToSupabase(false);
          loadFromLocalStorage();
        }
      } catch (err) {
        console.log('⚠️ Sin conexión a Supabase - Usando localStorage');
        setIsConnectedToSupabase(false);
        loadFromLocalStorage();
      } finally {
        setIsLoading(false);
        setDataLoaded(true);
      }
    };
    initializeData();
  }, []);
  // One-time: restore appointments that were auto-cancelled due to expiration back to 'programada'
  useEffect(() => {
    if (!dataLoaded || isLoading) return;
    const restoreExpiredAppointments = async () => {
      const expiredCancelled = appointments.filter(apt => apt.status === 'cancelada' && apt.cancelReason && apt.cancelReason.includes('vencida'));
      for (const apt of expiredCancelled) {
        try {
          await updateAppointment(apt.id, {
            status: 'programada',
            cancelReason: ''
          });
          console.log(`✅ Cita restaurada a programada: ${apt.id} - ${apt.date}`);
        } catch (err) {
          console.error('Error restaurando cita:', err);
        }
      }
      if (expiredCancelled.length > 0) {
        console.log(`✅ ${expiredCancelled.length} cita(s) restaurada(s) a programada`);
      }
    };
    restoreExpiredAppointments();
  }, [dataLoaded, isLoading]);
  // Save to localStorage as backup
  useEffect(() => {
    if (dataLoaded && !isLoading) {
      localStorage.setItem('savidna-patients', JSON.stringify(patients));
      localStorage.setItem('savidna-doctors', JSON.stringify(doctors));
      localStorage.setItem('savidna-appointments', JSON.stringify(appointments));
      localStorage.setItem('savidna-services', JSON.stringify(services));
      localStorage.setItem('savidna-insurances', JSON.stringify(insurances));
      localStorage.setItem('savidna-specialties', JSON.stringify(specialties));
      localStorage.setItem('savidna-contacts', JSON.stringify(contacts));
      localStorage.setItem('savidna-companies', JSON.stringify(companies));
      localStorage.setItem('savidna-opportunities', JSON.stringify(opportunities));
      localStorage.setItem('savidna-clinical-histories', JSON.stringify(clinicalHistories));
      localStorage.setItem('savidna-roles', JSON.stringify(roles));
      localStorage.setItem('savidna-permissions', JSON.stringify(permissions));
      localStorage.setItem('savidna-audit-logs', JSON.stringify(auditLogs));
      localStorage.setItem('savidna-follow-up-notes', JSON.stringify(followUpNotes));
    }
  }, [patients, doctors, appointments, services, insurances, specialties, contacts, companies, opportunities, clinicalHistories, roles, permissions, auditLogs, followUpNotes, dataLoaded, isLoading]);
  const loadFromSupabase = async () => {
    try {
      const [patientsData, doctorsData, appointmentsData, servicesData, insurancesData, specialtiesData, contactsData, companiesData, opportunitiesData, clinicalHistoriesData, rolesData, permissionsData] = await Promise.all([fetchAllRows('patients'), fetchAllRows('doctors'), fetchAllRows('appointments'), fetchAllRows('services'), fetchAllRows('insurances'), fetchAllRows('specialties'), fetchAllRows('contacts'), fetchAllRows('companies'), fetchAllRows('opportunities'), fetchAllRows('clinical_histories'), fetchAllRows('roles'), fetchAllRows('permissions')]);
      // Also load audit logs and follow-up notes (non-blocking)
      const [auditData, notesData] = await Promise.all([fetchAllRows('audit_logs', {
        column: 'created_at',
        ascending: false
      }), fetchAllRows('follow_up_notes', {
        column: 'created_at',
        ascending: false
      })]);
      // Use mapper functions for consistent data transformation
      if (patientsData) setPatients(patientsData.map(mapPatient));
      if (doctorsData) setDoctors(doctorsData.map(mapDoctor));
      if (appointmentsData) setAppointments(appointmentsData.map(mapAppointment));
      if (servicesData) setServices(servicesData.map(mapService));
      if (insurancesData) setInsurances(insurancesData.map(mapInsurance));
      if (specialtiesData) setSpecialties(specialtiesData);
      if (contactsData) setContacts(contactsData.map(mapContact));
      if (companiesData) setCompanies(companiesData.map(mapCompany));
      if (opportunitiesData) setOpportunities(opportunitiesData.map(mapOpportunity));
      if (clinicalHistoriesData) setClinicalHistories(clinicalHistoriesData.map(mapClinicalHistory));
      if (rolesData) {
        const loadedRoles = rolesData.map(mapRole);
        setRoles(loadedRoles);
        console.log('✅ Roles cargados desde Supabase:', loadedRoles.length);
      } else {
        const savedRoles = localStorage.getItem('savidna-roles');
        if (savedRoles) setRoles(JSON.parse(savedRoles));
      }
      if (permissionsData) {
        const loadedPermissions = permissionsData.map(mapPermission);
        setPermissions(loadedPermissions);
        // Sync Supabase permissions to localStorage for getRolePermissions()
        if (rolesData) {
          try {
            const allPermsMap: Record<string, Record<string, string>> = {};
            for (const perm of permissionsData) {
              const role = rolesData.find((r: any) => r.id === perm.role_id);
              if (role) {
                if (!allPermsMap[perm.module]) {
                  allPermsMap[perm.module] = {};
                }
                const normalizedName = normalizeRoleName(role.name);
                allPermsMap[perm.module][normalizedName] = perm.permission_level;
              }
            }
            localStorage.setItem('savidna-role-permissions', JSON.stringify(allPermsMap));
            console.log('✅ Permisos sincronizados a localStorage desde Supabase (nombres normalizados)');
            window.dispatchEvent(new Event('permissions-updated'));
          } catch (e) {
            console.error('Error syncing permissions to localStorage:', e);
          }
        }
      } else {
        const savedPermissions = localStorage.getItem('savidna-permissions');
        if (savedPermissions) setPermissions(JSON.parse(savedPermissions));
      }
      console.log('✅ Datos cargados desde Supabase');
      // ==================== SYNC STATUS FROM SUPABASE TO LOCALSTORAGE ====================
      const syncStatusToLocalStorage = (storageKey: string, items: {
        id: string;
        status?: string;
      }[] | null) => {
        if (!items) return;
        const statusMap: Record<string, 'archived' | 'deactivated'> = {};
        for (const item of items) {
          if (item.status === 'archived' || item.status === 'deactivated') {
            statusMap[item.id] = item.status;
          }
        }
        localStorage.setItem(storageKey, JSON.stringify(statusMap));
      };
      syncStatusToLocalStorage('savidna-patients-status', patientsData);
      syncStatusToLocalStorage('savidna-doctors-status', doctorsData);
      syncStatusToLocalStorage('savidna-services-status', servicesData);
      syncStatusToLocalStorage('savidna-specialties-status', specialtiesData);
      syncStatusToLocalStorage('savidna-insurance-status', insurancesData);
      syncStatusToLocalStorage('savidna-contacts-status', contactsData);
      syncStatusToLocalStorage('savidna-companies-status', companiesData);
      syncStatusToLocalStorage('savidna-opportunities-status', opportunitiesData);
      syncStatusToLocalStorage('savidna-clinical-history-status', clinicalHistoriesData);
      syncStatusToLocalStorage('savidna-followup-notes-status', notesData);
      console.log('✅ Estados de archivo/desactivación sincronizados desde Supabase');
      if (auditData) {
        setAuditLogs(auditData.map(mapAuditLog));
      } else {
        try {
          const savedLogs = JSON.parse(localStorage.getItem('savidna-audit-logs') || '[]');
          setAuditLogs(Array.isArray(savedLogs) ? savedLogs.map((a: any) => ({
            ...a,
            timestamp: new Date(a.timestamp || Date.now()),
            userId: typeof a.userId === 'string' ? a.userId : String(a.userId || ''),
            userName: typeof a.userName === 'string' ? a.userName : String(a.userName || ''),
            action: typeof a.action === 'string' ? a.action : String(a.action || ''),
            module: typeof a.module === 'string' ? a.module : String(a.module || ''),
            entityId: typeof a.entityId === 'string' ? a.entityId : String(a.entityId || ''),
            entityName: typeof a.entityName === 'string' ? a.entityName : String(a.entityName || ''),
            details: typeof a.details === 'string' ? a.details : String(a.details || '')
          })) : []);
        } catch {
          setAuditLogs([]);
        }
      }
      if (notesData) {
        setFollowUpNotes(notesData.map(mapFollowUpNote));
      } else {
        try {
          const savedNotes = JSON.parse(localStorage.getItem('savidna-follow-up-notes') || '[]');
          setFollowUpNotes(Array.isArray(savedNotes) ? savedNotes.map((n: any) => ({
            ...n,
            createdAt: new Date(n.createdAt || Date.now()),
            userId: typeof n.userId === 'string' ? n.userId : String(n.userId || ''),
            userName: typeof n.userName === 'string' ? n.userName : String(n.userName || ''),
            contactName: typeof n.contactName === 'string' ? n.contactName : String(n.contactName || ''),
            subject: typeof n.subject === 'string' ? n.subject : String(n.subject || ''),
            description: typeof n.description === 'string' ? n.description : String(n.description || '')
          })) : []);
        } catch {
          setFollowUpNotes([]);
        }
      }
    } catch (error) {
      console.error('Error loading from Supabase:', error);
      loadFromLocalStorage();
    }
  };
  const loadFromLocalStorage = () => {
    const loadFromStorage = <T,>(key: string, defaultValue: T[]): T[] => {
      const saved = localStorage.getItem(key);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return parsed.length > 0 ? parsed : defaultValue;
        } catch (e) {
          return defaultValue;
        }
      }
      return defaultValue;
    };
    const initialData = getInitialData();
    setPatients(loadFromStorage('savidna-patients', initialData.patients));
    setDoctors(loadFromStorage('savidna-doctors', initialData.doctors));
    setAppointments(loadFromStorage('savidna-appointments', initialData.appointments));
    setServices(loadFromStorage('savidna-services', initialData.services).map((s: any) => ({
      ...s,
      price: Math.round(Number(s.price)),
      duration: Math.round(Number(s.duration))
    })));
    setInsurances(loadFromStorage('savidna-insurances', initialData.insurances));
    setSpecialties(loadFromStorage('savidna-specialties', initialData.specialties));
    setContacts(loadFromStorage('savidna-contacts', initialData.contacts));
    setCompanies(loadFromStorage('savidna-companies', initialData.companies));
    setOpportunities(loadFromStorage('savidna-opportunities', initialData.opportunities));
    setClinicalHistories(loadFromStorage('savidna-clinical-histories', []));
    setRoles(loadFromStorage('savidna-roles', []));
    setPermissions(loadFromStorage('savidna-permissions', []));
    setAuditLogs(loadFromStorage('savidna-audit-logs', []));
    setFollowUpNotes(loadFromStorage('savidna-follow-up-notes', []));
    console.log('✅ Datos cargados desde localStorage');
  };
  // ==================== PATIENTS ====================
  const addPatient = async (patientData: Omit<Patient, 'id' | 'medicalHistory' | 'interactions' | 'createdAt'>): Promise<Patient> => {
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('patients').insert({
        name: patientData.name,
        gender: patientData.gender,
        phone: patientData.phone,
        email: patientData.email,
        address: patientData.address,
        emergency_contact: patientData.emergencyContact,
        insurance_id: patientData.insuranceId,
        medical_history: {},
        interactions: []
      }).select().single();
      if (error) {
        console.error('Error creando paciente en Supabase:', error);
        throw new Error(error.message);
      }
      const newPatient: Patient = {
        id: data.id,
        name: data.name,
        cedula: '',
        birthDate: '',
        age: 0,
        gender: data.gender,
        phone: data.phone,
        email: data.email,
        address: data.address,
        emergencyContact: data.emergency_contact,
        insuranceId: data.insurance_id,
        medicalHistory: data.medical_history || {},
        interactions: data.interactions || [],
        createdAt: new Date(data.created_at)
      };
      setPatients(prev => [...prev, newPatient]);
      logAction('create', 'patients', newPatient.id, newPatient.name, `Paciente "${newPatient.name}" creado`);
      return newPatient;
    } else {
      const newPatient: Patient = {
        ...patientData,
        id: crypto.randomUUID(),
        medicalHistory: {} as MedicalHistory,
        interactions: [],
        createdAt: new Date()
      };
      setPatients(prev => [...prev, newPatient]);
      logAction('create', 'patients', newPatient.id, newPatient.name, `Paciente "${newPatient.name}" creado`);
      return newPatient;
    }
  };
  const updatePatient = async (id: string, updates: Partial<Patient>) => {
    const existing = patients.find(p => p.id === id);
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {};
      if (updates.name !== undefined) supabaseUpdates.name = updates.name;
      if (updates.gender !== undefined) supabaseUpdates.gender = updates.gender;
      if (updates.phone !== undefined) supabaseUpdates.phone = updates.phone;
      if (updates.email !== undefined) supabaseUpdates.email = updates.email;
      if (updates.address !== undefined) supabaseUpdates.address = updates.address;
      if (updates.emergencyContact !== undefined) supabaseUpdates.emergency_contact = updates.emergencyContact;
      if (updates.insuranceId !== undefined) supabaseUpdates.insurance_id = updates.insuranceId;
      if (updates.medicalHistory !== undefined) supabaseUpdates.medical_history = updates.medicalHistory;
      if (updates.interactions !== undefined) supabaseUpdates.interactions = updates.interactions;
      if ((updates as any).status !== undefined) supabaseUpdates.status = (updates as any).status;
      if (Object.keys(supabaseUpdates).length > 0) {
        const {
          error
        } = await supabase.from('patients').update(supabaseUpdates).eq('id', id);
        if (error) throw error;
      }
    }
    setPatients(prev => prev.map(p => p.id === id ? {
      ...p,
      ...updates
    } : p));
    if (existing) {
      logAction('update', 'patients', id, existing.name, `Paciente "${existing.name}" actualizado`);
    }
  };
  const deletePatient = async (id: string) => {
    const existing = patients.find(p => p.id === id);
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('patients').delete().eq('id', id);
      if (error) throw error;
    }
    setPatients(prev => prev.filter(p => p.id !== id));
    if (existing) {
      logAction('delete', 'patients', id, existing.name, `Paciente "${existing.name}" eliminado`);
    }
  };
  const getPatient = (id: string) => patients.find(p => p.id === id);
  const updateMedicalHistory = async (patientId: string, history: Partial<MedicalHistory>) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;
    const updatedHistory = {
      ...patient.medicalHistory,
      ...history
    };
    await updatePatient(patientId, {
      medicalHistory: updatedHistory
    });
  };
  const addMedicalEntry = async (patientId: string, entry: Omit<MedicalEntry, 'id' | 'date'>) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;
    const newEntry: MedicalEntry = {
      ...entry,
      id: crypto.randomUUID(),
      date: new Date()
    };
    const updatedHistory = {
      ...patient.medicalHistory,
      entries: [...(patient.medicalHistory.entries || []), newEntry]
    };
    await updatePatient(patientId, {
      medicalHistory: updatedHistory
    });
  };
  const addPatientInteraction = async (patientId: string, interaction: Omit<Interaction, 'id' | 'date'>) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;
    const newInteraction: Interaction = {
      ...interaction,
      id: crypto.randomUUID(),
      date: new Date()
    };
    await updatePatient(patientId, {
      interactions: [...patient.interactions, newInteraction]
    });
  };
  // ==================== DOCTORS ====================
  const addDoctor = async (doctorData: Omit<Doctor, 'id' | 'createdAt'>): Promise<Doctor> => {
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('doctors').insert({
        name: doctorData.name,
        gender: doctorData.gender || 'masculino',
        specialty_id: doctorData.specialtyId,
        phone: doctorData.phone,
        email: doctorData.email,
        schedule: doctorData.schedule
      }).select().single();
      if (error) throw error;
      const newDoctor: Doctor = {
        id: data.id,
        name: data.name,
        gender: data.gender || 'masculino',
        specialtyId: data.specialty_id,
        phone: data.phone,
        email: data.email,
        schedule: data.schedule,
        createdAt: new Date(data.created_at)
      };
      setDoctors(prev => [...prev, newDoctor]);
      logAction('create', 'doctors', newDoctor.id, newDoctor.name, `Doctor "${newDoctor.gender === 'femenino' ? 'Dra.' : 'Dr.'} ${newDoctor.name}" creado`);
      return newDoctor;
    } else {
      const newDoctor: Doctor = {
        ...doctorData,
        id: crypto.randomUUID(),
        createdAt: new Date()
      };
      setDoctors(prev => [...prev, newDoctor]);
      logAction('create', 'doctors', newDoctor.id, newDoctor.name, `Doctor "${newDoctor.gender === 'femenino' ? 'Dra.' : 'Dr.'} ${newDoctor.name}" creado`);
      return newDoctor;
    }
  };
  const updateDoctor = async (id: string, updates: Partial<Doctor>) => {
    const existing = doctors.find(d => d.id === id);
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {};
      if (updates.name !== undefined) supabaseUpdates.name = updates.name;
      if (updates.gender !== undefined) supabaseUpdates.gender = updates.gender;
      if (updates.specialtyId !== undefined) supabaseUpdates.specialty_id = updates.specialtyId;
      if (updates.phone !== undefined) supabaseUpdates.phone = updates.phone;
      if (updates.email !== undefined) supabaseUpdates.email = updates.email;
      if (updates.schedule !== undefined) supabaseUpdates.schedule = updates.schedule;
      if ((updates as any).status !== undefined) supabaseUpdates.status = (updates as any).status;
      if (Object.keys(supabaseUpdates).length > 0) {
        const {
          error
        } = await supabase.from('doctors').update(supabaseUpdates).eq('id', id);
        if (error) throw error;
      }
    }
    setDoctors(prev => prev.map(d => d.id === id ? {
      ...d,
      ...updates
    } : d));
    if (existing) {
      logAction('update', 'doctors', id, existing.name, `Doctor "${existing.gender === 'femenino' ? 'Dra.' : 'Dr.'} ${existing.name}" actualizado`);
    }
  };
  const deleteDoctor = async (id: string) => {
    const existing = doctors.find(d => d.id === id);
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('doctors').delete().eq('id', id);
      if (error) throw error;
    }
    setDoctors(prev => prev.filter(d => d.id !== id));
    if (existing) {
      logAction('delete', 'doctors', id, existing.name, `Doctor "${existing.name}" eliminado`);
    }
  };
  const getDoctor = (id: string) => doctors.find(d => d.id === id);
  const getDoctorsBySpecialty = (specialtyId: string) => doctors.filter(d => d.specialtyId === specialtyId);
  // ==================== APPOINTMENTS ====================
  const addAppointment = async (appointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>): Promise<Appointment> => {
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('appointments').insert({
        patient_id: appointmentData.patientId,
        doctor_id: appointmentData.doctorId,
        service_id: appointmentData.serviceId,
        service_ids: appointmentData.serviceIds || [],
        date: appointmentData.date,
        time: appointmentData.time,
        duration: appointmentData.duration,
        status: appointmentData.status,
        notes: appointmentData.notes,
        created_by_user_id: appointmentData.createdByUserId || user?.id || null,
        created_by_user_name: appointmentData.createdByUserName || user?.name || null
      }).select().single();
      if (error) throw error;
      const newAppointment: Appointment = {
        id: data.id,
        patientId: data.patient_id,
        doctorId: data.doctor_id,
        serviceId: data.service_id,
        serviceIds: data.service_ids || [],
        date: data.date,
        time: data.time,
        duration: data.duration,
        status: data.status,
        notes: data.notes || '',
        createdByUserId: data.created_by_user_id || '',
        createdByUserName: data.created_by_user_name || '',
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };
      setAppointments(prev => [...prev, newAppointment]);
      return newAppointment;
    } else {
      const newAppointment: Appointment = {
        ...appointmentData,
        id: crypto.randomUUID(),
        serviceIds: appointmentData.serviceIds || [appointmentData.serviceId],
        createdByUserId: appointmentData.createdByUserId || user?.id || '',
        createdByUserName: appointmentData.createdByUserName || user?.name || '',
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setAppointments(prev => [...prev, newAppointment]);
      return newAppointment;
    }
  };
  const updateAppointment = async (id: string, updates: Partial<Appointment>) => {
    if (isConnectedToSupabase) {
      // Build update object, converting empty strings to null for DATE columns
      const supabaseUpdates: Record<string, any> = {};
      if (updates.patientId !== undefined) supabaseUpdates.patient_id = updates.patientId;
      if (updates.doctorId !== undefined) supabaseUpdates.doctor_id = updates.doctorId;
      if (updates.serviceId !== undefined) supabaseUpdates.service_id = updates.serviceId;
      if (updates.serviceIds !== undefined) supabaseUpdates.service_ids = updates.serviceIds; // Add service_ids
      if (updates.date !== undefined) supabaseUpdates.date = updates.date;
      if (updates.time !== undefined) supabaseUpdates.time = updates.time;
      if (updates.duration !== undefined) supabaseUpdates.duration = updates.duration;
      if (updates.status !== undefined) supabaseUpdates.status = updates.status;
      if (updates.notes !== undefined) supabaseUpdates.notes = updates.notes;
      // DATE columns: convert empty strings to null
      if (updates.nextAppointmentDate !== undefined) {
        supabaseUpdates.next_appointment_date = updates.nextAppointmentDate && updates.nextAppointmentDate !== '' ? updates.nextAppointmentDate : null;
      }
      if (updates.nextAppointmentNotes !== undefined) {
        supabaseUpdates.next_appointment_notes = updates.nextAppointmentNotes || null;
      }
      if (updates.cancelReason !== undefined) {
        supabaseUpdates.cancel_reason = updates.cancelReason || null;
      }
      if (updates.originalDate !== undefined) {
        supabaseUpdates.original_date = updates.originalDate && updates.originalDate !== '' ? updates.originalDate : null;
      }
      if (updates.originalTime !== undefined) {
        supabaseUpdates.original_time = updates.originalTime || null;
      }
      const {
        error
      } = await supabase.from('appointments').update(supabaseUpdates).eq('id', id);
      if (error) {
        console.error('❌ Error actualizando cita en Supabase:', error);
        throw error;
      }
    }
    setAppointments(prev => prev.map(a => a.id === id ? {
      ...a,
      ...updates,
      updatedAt: new Date()
    } : a));
  };
  const cancelAppointment = async (id: string, reason: string) => {
    const existing = appointments.find(a => a.id === id);
    await updateAppointment(id, {
      status: 'cancelada',
      cancelReason: reason
    });
    if (existing) {
      const patient = patients.find(p => p.id === existing.patientId);
      logAction('cancel', 'appointments', id, patient?.name || 'Cita', `Cita de "${patient?.name}" cancelada. Motivo: ${reason}`);
    }
  };
  const rescheduleAppointment = async (id: string, newDate: string, newTime: string) => {
    const appointment = appointments.find(a => a.id === id);
    if (!appointment) return;
    const patient = patients.find(p => p.id === appointment.patientId);
    await updateAppointment(id, {
      date: newDate,
      time: newTime,
      status: 'reprogramada',
      originalDate: appointment.date,
      originalTime: appointment.time
    });
    logAction('reschedule', 'appointments', id, patient?.name || 'Cita', `Cita de "${patient?.name}" reprogramada de ${appointment.date} ${appointment.time} a ${newDate} ${newTime}`);
  };
  const completeAppointment = async (id: string, nextAppointmentDate?: string, nextAppointmentNotes?: string) => {
    const existing = appointments.find(a => a.id === id);
    // Convert empty strings to undefined to avoid Supabase DATE column errors
    const cleanNextDate = nextAppointmentDate && nextAppointmentDate.trim() !== '' ? nextAppointmentDate : undefined;
    const cleanNextNotes = nextAppointmentNotes && nextAppointmentNotes.trim() !== '' ? nextAppointmentNotes : undefined;
    await updateAppointment(id, {
      status: 'completada',
      nextAppointmentDate: cleanNextDate || null,
      nextAppointmentNotes: cleanNextNotes || null
    });
    if (existing) {
      const patient = patients.find(p => p.id === existing.patientId);
      const doctor = doctors.find(d => d.id === existing.doctorId);
      // Handle multiple services for opportunity creation
      const serviceIds = existing.serviceIds && existing.serviceIds.length > 0 ? existing.serviceIds : [existing.serviceId];
      const selectedServices = services.filter(s => serviceIds.includes(s.id));
      const serviceNames = selectedServices.map(s => s.name).join(', ');
      const totalAmount = selectedServices.reduce((sum, s) => sum + (s.price || 0), 0);
      const nextInfo = cleanNextDate ? ` Próxima cita: ${cleanNextDate}` : '';
      logAction('complete', 'appointments', id, patient?.name || 'Cita', `Cita de "${patient?.name}" completada.${nextInfo}`);
      // AUTO-GENERATE OPPORTUNITY (SALE) when completing an appointment
      if (patient && selectedServices.length > 0) {
        try {
          const opportunityName = `${serviceNames} - ${patient.name}`;
          await addOpportunity({
            name: opportunityName,
            contactId: null,
            contactSource: '',
            responsibleUserId: null,
            opportunityType: '',
            stage: 'servicio_realizado',
            amount: totalAmount,
            probability: 100,
            notes: `Generado automáticamente al completar cita.\nPaciente: ${patient.name}\nDoctor: Dr. ${doctor?.name || 'N/A'}\nServicios: ${serviceNames}\nFecha: ${existing.date} ${existing.time}`
          });
          console.log('✅ Oportunidad/venta generada automáticamente:', opportunityName, formatCurrency(totalAmount));
        } catch (err) {
          console.error('❌ Error generando oportunidad automática:', err);
        }
      }
    }
    if (cleanNextDate) {
      const appointment = appointments.find(a => a.id === id);
      if (appointment) {
        // Create next appointment with same services by default? Or empty?
        // Usually follow-up is consultation, but let's copy services for now or just pick first
        // User didn't specify, so let's keep it simple: copy services
        await addAppointment({
          patientId: appointment.patientId,
          doctorId: appointment.doctorId,
          serviceId: appointment.serviceId,
          serviceIds: appointment.serviceIds,
          date: cleanNextDate,
          time: appointment.time,
          duration: appointment.duration,
          status: 'programada',
          notes: cleanNextNotes || 'Cita de seguimiento'
        });
      }
    }
  };
  const getAppointment = (id: string) => appointments.find(a => a.id === id);
  const getAppointmentsByDate = (date: string) => appointments.filter(a => a.date === date);
  const getAppointmentsByPatient = (patientId: string) => appointments.filter(a => a.patientId === patientId);
  const getAppointmentsByDoctor = (doctorId: string) => appointments.filter(a => a.doctorId === doctorId);
  const checkDoctorAvailability = (doctorId: string, date: string, time: string, excludeId?: string): boolean => {
    const BUFFER_MINUTES = 30;
    // Helper: convert "HH:MM" to total minutes
    const toMinutes = (t: string): number => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    // 1. Check doctor's schedule for that day
    const doctor = doctors.find(d => d.id === doctorId);
    if (doctor && doctor.schedule) {
      const dayKey = getDayOfWeekKey(date) as keyof typeof doctor.schedule;
      const daySchedule = doctor.schedule[dayKey];
      // If doctor doesn't work that day
      if (!daySchedule) {
        return false;
      }
      // Check if time is within working hours
      const requestedMinutes = toMinutes(time);
      const startMinutes = toMinutes(daySchedule.start);
      const endMinutes = toMinutes(daySchedule.end);
      if (requestedMinutes < startMinutes || requestedMinutes >= endMinutes) {
        return false;
      }
    }
    // 2. Check for conflicting appointments (with 30-min buffer)
    const existingAppointments = appointments.filter(a => a.doctorId === doctorId && a.date === date && a.status !== 'cancelada' && a.status !== 'completada' && a.id !== excludeId);
    const requestedStart = toMinutes(time);
    for (const apt of existingAppointments) {
      const aptStart = toMinutes(apt.time);
      const aptEnd = aptStart + (apt.duration || 30) + BUFFER_MINUTES; // appointment end + 30 min buffer
      // The new appointment's start time must not fall within [aptStart, aptEnd)
      // Also check if the existing appointment would start during the new one
      // (i.e., the existing apt starts before the new one ends)
      if (requestedStart >= aptStart && requestedStart < aptEnd) {
        return false;
      }
      // Reverse check: does the existing appointment fall within the new appointment's window?
      // We don't know the new duration here, but at minimum block if existing starts within buffer before requested time
      if (aptStart > requestedStart && aptStart < requestedStart + BUFFER_MINUTES) {
        return false;
      }
    }
    return true;
  };
  const syncAllAppointmentsToSheets = async () => {
    console.log('Sync deprecated');
  };
  // ==================== SERVICES ====================
  const addService = async (serviceData: Omit<Service, 'id'>): Promise<Service> => {
    // Ensure price is always a clean integer before saving
    const cleanData = {
      ...serviceData,
      price: Math.round(Number(serviceData.price)),
      duration: Math.round(Number(serviceData.duration))
    };
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('services').insert({
        name: cleanData.name,
        sku: cleanData.sku || null,
        duration: cleanData.duration,
        price: cleanData.price,
        description: cleanData.description,
        doctor_ids: cleanData.doctorIds || []
      }).select().single();
      if (error) throw error;
      const newService: Service = {
        id: data.id,
        name: data.name,
        sku: data.sku || '',
        duration: Math.round(Number(data.duration)),
        price: Math.round(Number(data.price)),
        description: data.description || '',
        doctorIds: data.doctor_ids || []
      };
      setServices(prev => [...prev, newService]);
      logAction('create', 'services', newService.id, newService.name, `Servicio "${newService.name}" creado`);
      return newService;
    } else {
      const newService: Service = {
        ...cleanData,
        id: crypto.randomUUID()
      };
      setServices(prev => [...prev, newService]);
      logAction('create', 'services', newService.id, newService.name, `Servicio "${newService.name}" creado`);
      return newService;
    }
  };
  const updateService = async (id: string, updates: Partial<Service>) => {
    const existing = services.find(s => s.id === id);
    // Ensure price is always a clean integer
    const cleanUpdates = {
      ...updates,
      price: updates.price !== undefined ? Math.round(Number(updates.price)) : undefined,
      duration: updates.duration !== undefined ? Math.round(Number(updates.duration)) : undefined
    };
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {};
      if (cleanUpdates.name !== undefined) supabaseUpdates.name = cleanUpdates.name;
      if (cleanUpdates.sku !== undefined) supabaseUpdates.sku = cleanUpdates.sku || null;
      if (cleanUpdates.duration !== undefined) supabaseUpdates.duration = cleanUpdates.duration;
      if (cleanUpdates.price !== undefined) supabaseUpdates.price = cleanUpdates.price;
      if (cleanUpdates.description !== undefined) supabaseUpdates.description = cleanUpdates.description;
      if (cleanUpdates.doctorIds !== undefined) supabaseUpdates.doctor_ids = cleanUpdates.doctorIds;
      if ((cleanUpdates as any).status !== undefined) supabaseUpdates.status = (cleanUpdates as any).status;
      if (Object.keys(supabaseUpdates).length > 0) {
        const {
          error
        } = await supabase.from('services').update(supabaseUpdates).eq('id', id);
        if (error) throw error;
      }
    }
    setServices(prev => prev.map(s => s.id === id ? {
      ...s,
      ...cleanUpdates
    } : s));
    if (existing) {
      logAction('update', 'services', id, existing.name, `Servicio "${existing.name}" actualizado`);
    }
  };
  const deleteService = async (id: string) => {
    const existing = services.find(s => s.id === id);
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('services').delete().eq('id', id);
      if (error) throw error;
    }
    setServices(prev => prev.filter(s => s.id !== id));
    if (existing) {
      logAction('delete', 'services', id, existing.name, `Servicio "${existing.name}" eliminado`);
    }
  };
  const getService = (id: string) => services.find(s => s.id === id);
  // ==================== INSURANCES ====================
  const addInsurance = async (insuranceData: Omit<Insurance, 'id'>): Promise<Insurance> => {
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('insurances').insert({
        name: insuranceData.name,
        company: insuranceData.company,
        coverage_type: insuranceData.coverageType,
        phone: insuranceData.phone,
        email: insuranceData.email
      }).select().single();
      if (error) throw error;
      const newInsurance: Insurance = {
        id: data.id,
        name: data.name,
        company: data.company,
        coverageType: data.coverage_type,
        phone: data.phone,
        email: data.email
      };
      setInsurances(prev => [...prev, newInsurance]);
      return newInsurance;
    } else {
      const newInsurance: Insurance = {
        ...insuranceData,
        id: crypto.randomUUID()
      };
      setInsurances(prev => [...prev, newInsurance]);
      return newInsurance;
    }
  };
  const updateInsurance = async (id: string, updates: Partial<Insurance>) => {
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {};
      if (updates.name !== undefined) supabaseUpdates.name = updates.name;
      if (updates.company !== undefined) supabaseUpdates.company = updates.company;
      if (updates.coverageType !== undefined) supabaseUpdates.coverage_type = updates.coverageType;
      if (updates.phone !== undefined) supabaseUpdates.phone = updates.phone;
      if (updates.email !== undefined) supabaseUpdates.email = updates.email;
      if ((updates as any).status !== undefined) supabaseUpdates.status = (updates as any).status;
      if (Object.keys(supabaseUpdates).length > 0) {
        const {
          error
        } = await supabase.from('insurances').update(supabaseUpdates).eq('id', id);
        if (error) throw error;
      }
    }
    setInsurances(prev => prev.map(i => i.id === id ? {
      ...i,
      ...updates
    } : i));
  };
  const deleteInsurance = async (id: string) => {
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('insurances').delete().eq('id', id);
      if (error) throw error;
    }
    setInsurances(prev => prev.filter(i => i.id !== id));
  };
  const getInsurance = (id: string) => insurances.find(i => i.id === id);
  // ==================== SPECIALTIES ====================
  const addSpecialty = async (specialtyData: Omit<Specialty, 'id'>): Promise<Specialty> => {
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('specialties').insert(specialtyData).select().single();
      if (error) throw error;
      setSpecialties(prev => [...prev, data]);
      return data;
    } else {
      const newSpecialty: Specialty = {
        ...specialtyData,
        id: crypto.randomUUID()
      };
      setSpecialties(prev => [...prev, newSpecialty]);
      return newSpecialty;
    }
  };
  const updateSpecialty = async (id: string, updates: Partial<Specialty>) => {
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {
        ...updates
      };
      if ((updates as any).status !== undefined) supabaseUpdates.status = (updates as any).status;
      const {
        error
      } = await supabase.from('specialties').update(supabaseUpdates).eq('id', id);
      if (error) throw error;
    }
    setSpecialties(prev => prev.map(s => s.id === id ? {
      ...s,
      ...updates
    } : s));
  };
  const deleteSpecialty = async (id: string) => {
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('specialties').delete().eq('id', id);
      if (error) throw error;
    }
    setSpecialties(prev => prev.filter(s => s.id !== id));
  };
  const getSpecialty = (id: string) => specialties.find(s => s.id === id);
  // ==================== CONTACTS ====================
  const addContact = async (contactData: Omit<Contact, 'id' | 'interactions' | 'createdAt'>): Promise<Contact> => {
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('contacts').insert({
        name: contactData.name,
        phone: contactData.phone,
        email: contactData.email,
        contact_status: contactData.contactStatus || 'nuevo',
        contact_source: contactData.contactSource || 'otro',
        notes: contactData.notes,
        created_by: contactData.createdBy || null,
        interactions: []
      }).select().single();
      if (error) throw error;
      const newContact: Contact = {
        id: data.id,
        name: data.name,
        phone: data.phone,
        email: data.email,
        company: '',
        position: '',
        contactStatus: (data.contact_status || 'nuevo') as ContactStatus,
        contactSource: (data.contact_source || 'otro') as ContactSource,
        notes: data.notes || '',
        interactions: data.interactions || [],
        createdAt: new Date(data.created_at),
        createdBy: data.created_by || contactData.createdBy || ''
      };
      setContacts(prev => [...prev, newContact]);
      logAction('create', 'contacts', newContact.id, newContact.name, `Contacto "${newContact.name}" creado`);
      return newContact;
    } else {
      const newContact: Contact = {
        ...contactData,
        id: crypto.randomUUID(),
        interactions: [],
        createdAt: new Date()
      };
      setContacts(prev => [...prev, newContact]);
      logAction('create', 'contacts', newContact.id, newContact.name, `Contacto "${newContact.name}" creado`);
      return newContact;
    }
  };
  const updateContact = async (id: string, updates: Partial<Contact>) => {
    const existing = contacts.find(c => c.id === id);
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {};
      if (updates.name !== undefined) supabaseUpdates.name = updates.name;
      if (updates.phone !== undefined) supabaseUpdates.phone = updates.phone;
      if (updates.email !== undefined) supabaseUpdates.email = updates.email;
      if (updates.contactStatus !== undefined) supabaseUpdates.contact_status = updates.contactStatus;
      if (updates.contactSource !== undefined) supabaseUpdates.contact_source = updates.contactSource;
      if (updates.notes !== undefined) supabaseUpdates.notes = updates.notes;
      if (updates.interactions !== undefined) supabaseUpdates.interactions = updates.interactions;
      if ((updates as any).status !== undefined) supabaseUpdates.status = (updates as any).status;
      if (Object.keys(supabaseUpdates).length > 0) {
        const {
          error
        } = await supabase.from('contacts').update(supabaseUpdates).eq('id', id);
        if (error) throw error;
      }
    }
    setContacts(prev => prev.map(c => c.id === id ? {
      ...c,
      ...updates
    } : c));
    if (existing) {
      logAction('update', 'contacts', id, existing.name, `Contacto "${existing.name}" actualizado`);
    }
  };
  const deleteContact = async (id: string) => {
    const existing = contacts.find(c => c.id === id);
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('contacts').delete().eq('id', id);
      if (error) throw error;
    }
    setContacts(prev => prev.filter(c => c.id !== id));
    if (existing) {
      logAction('delete', 'contacts', id, existing.name, `Contacto "${existing.name}" eliminado`);
    }
  };
  const getContact = (id: string) => contacts.find(c => c.id === id);
  const addContactInteraction = async (contactId: string, interaction: Omit<Interaction, 'id' | 'date'>) => {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;
    const newInteraction: Interaction = {
      ...interaction,
      id: crypto.randomUUID(),
      date: new Date()
    };
    await updateContact(contactId, {
      interactions: [...contact.interactions, newInteraction]
    });
  };
  // ==================== COMPANIES ====================
  const addCompany = async (companyData: Omit<Company, 'id' | 'createdAt'>): Promise<Company> => {
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('companies').insert({
        name: companyData.name,
        industry: companyData.industry,
        size: companyData.size,
        location: companyData.location,
        phone: companyData.phone,
        email: companyData.email,
        website: companyData.website,
        notes: companyData.notes
      }).select().single();
      if (error) throw error;
      const newCompany: Company = {
        id: data.id,
        name: data.name,
        industry: data.industry,
        size: data.size,
        location: data.location,
        phone: data.phone,
        email: data.email,
        website: data.website,
        notes: data.notes || '',
        createdAt: new Date(data.created_at)
      };
      setCompanies(prev => [...prev, newCompany]);
      return newCompany;
    } else {
      const newCompany: Company = {
        ...companyData,
        id: crypto.randomUUID(),
        createdAt: new Date()
      };
      setCompanies(prev => [...prev, newCompany]);
      return newCompany;
    }
  };
  const updateCompany = async (id: string, updates: Partial<Company>) => {
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {
        ...updates
      };
      if ((updates as any).status !== undefined) supabaseUpdates.status = (updates as any).status;
      // Remove frontend-only fields
      delete supabaseUpdates.createdAt;
      const {
        error
      } = await supabase.from('companies').update(supabaseUpdates).eq('id', id);
      if (error) throw error;
    }
    setCompanies(prev => prev.map(c => c.id === id ? {
      ...c,
      ...updates
    } : c));
  };
  const deleteCompany = async (id: string) => {
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('companies').delete().eq('id', id);
      if (error) throw error;
    }
    setCompanies(prev => prev.filter(c => c.id !== id));
  };
  const getCompany = (id: string) => companies.find(c => c.id === id);
  // ==================== OPPORTUNITIES ====================
  const addOpportunity = async (opportunityData: Omit<Opportunity, 'id' | 'interactions' | 'createdAt' | 'updatedAt'>): Promise<Opportunity> => {
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('opportunities').insert({
        name: opportunityData.name,
        contact_id: opportunityData.contactId,
        contact_source: opportunityData.contactSource || '',
        responsible_user_id: opportunityData.responsibleUserId || null,
        opportunity_type: opportunityData.opportunityType || '',
        stage: opportunityData.stage,
        amount: opportunityData.amount,
        probability: opportunityData.probability,
        notes: opportunityData.notes,
        interactions: []
      }).select().single();
      if (error) throw error;
      const newOpportunity: Opportunity = {
        id: data.id,
        name: data.name,
        contactId: data.contact_id,
        contactSource: data.contact_source || '',
        responsibleUserId: data.responsible_user_id || null,
        opportunityType: data.opportunity_type || '',
        stage: data.stage,
        amount: data.amount,
        probability: data.probability,
        notes: data.notes || '',
        interactions: data.interactions || [],
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };
      setOpportunities(prev => [...prev, newOpportunity]);
      return newOpportunity;
    } else {
      const newOpportunity: Opportunity = {
        ...opportunityData,
        id: crypto.randomUUID(),
        interactions: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setOpportunities(prev => [...prev, newOpportunity]);
      return newOpportunity;
    }
  };
  const updateOpportunity = async (id: string, updates: Partial<Opportunity>) => {
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {};
      if (updates.name !== undefined) supabaseUpdates.name = updates.name;
      if (updates.contactId !== undefined) supabaseUpdates.contact_id = updates.contactId;
      if (updates.contactSource !== undefined) supabaseUpdates.contact_source = updates.contactSource;
      if ((updates as any).responsibleUserId !== undefined) supabaseUpdates.responsible_user_id = (updates as any).responsibleUserId;
      if ((updates as any).opportunityType !== undefined) supabaseUpdates.opportunity_type = (updates as any).opportunityType;
      if (updates.stage !== undefined) supabaseUpdates.stage = updates.stage;
      if (updates.amount !== undefined) supabaseUpdates.amount = updates.amount;
      if (updates.probability !== undefined) supabaseUpdates.probability = updates.probability;
      if (updates.notes !== undefined) supabaseUpdates.notes = updates.notes;
      if (updates.interactions !== undefined) supabaseUpdates.interactions = updates.interactions;
      if ((updates as any).status !== undefined) supabaseUpdates.status = (updates as any).status;
      if (Object.keys(supabaseUpdates).length > 0) {
        const {
          error
        } = await supabase.from('opportunities').update(supabaseUpdates).eq('id', id);
        if (error) throw error;
      }
    }
    setOpportunities(prev => prev.map(o => o.id === id ? {
      ...o,
      ...updates,
      updatedAt: new Date()
    } : o));
  };
  const deleteOpportunity = async (id: string) => {
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('opportunities').delete().eq('id', id);
      if (error) throw error;
    }
    setOpportunities(prev => prev.filter(o => o.id !== id));
  };
  const getOpportunity = (id: string) => opportunities.find(o => o.id === id);
  const addOpportunityInteraction = async (opportunityId: string, interaction: Omit<Interaction, 'id' | 'date'>) => {
    const opportunity = opportunities.find(o => o.id === opportunityId);
    if (!opportunity) return;
    const newInteraction: Interaction = {
      ...interaction,
      id: crypto.randomUUID(),
      date: new Date()
    };
    await updateOpportunity(opportunityId, {
      interactions: [...opportunity.interactions, newInteraction]
    });
  };
  // ==================== CLINICAL HISTORIES ====================
  const addClinicalHistory = async (historyData: Omit<ClinicalHistory, 'id' | 'createdAt' | 'updatedAt'>): Promise<ClinicalHistory> => {
    if (isConnectedToSupabase) {
      // Ensure date is sent as a date string in local timezone (not UTC)
      let dateValue: string;
      if (historyData.date instanceof Date) {
        dateValue = historyData.date.toLocaleDateString('en-CA', {
          timeZone: 'America/Santo_Domingo'
        });
      } else {
        dateValue = historyData.date;
      }
      const physExam = historyData.physicalExam || {};
      // Build the base insert object (without physical exam columns)
      const baseInsert: Record<string, any> = {
        patient_id: historyData.patientId,
        date: dateValue,
        doctor_id: historyData.doctorId,
        doctor_name: historyData.doctorName,
        personal_background: historyData.personalBackground,
        clinical_background: historyData.clinicalBackground,
        diagnosis: historyData.diagnosis,
        treatment: historyData.treatment,
        medical_indications: historyData.medicalIndications,
        sessions: historyData.sessions,
        session_services: historyData.sessionServices || [],
        comments: historyData.comments,
        doctor_signature: historyData.doctorSignature,
        attached_images: historyData.attachedImages || []
      };
      // Physical exam columns (may not exist if migration wasn't run)
      const physExamColumns: Record<string, any> = {
        blood_pressure: physExam.bloodPressure || '',
        blood_sugar: physExam.bloodSugar || '',
        heart_rate: physExam.heartRate || '',
        lungs: physExam.lungs || '',
        eyes: physExam.eyes || '',
        throat: physExam.throat || '',
        abdomen: physExam.abdomen || '',
        weight: physExam.weight || '',
        height: physExam.height || '',
        temperature: physExam.temperature || '',
        positive_aspects: (physExam as any).positiveAspects || '',
        respiratory_rate: (physExam as any).respiratoryRate || '',
        weight_lb: (physExam as any).weightLb || '',
        // Historia de la Enfermedad Actual
        disease_history_since_when: (historyData as any).diseaseHistory?.sinceWhen || '',
        disease_history_previous_treatment: (historyData as any).diseaseHistory?.previousTreatment || '',
        disease_history_evolution: (historyData as any).diseaseHistory?.evolution || '',
        // Revisión por Sistemas
        systems_review_general: (historyData as any).systemsReview?.general || '',
        systems_review_cardio_respiratory: (historyData as any).systemsReview?.cardioRespiratory || '',
        systems_review_gastrointestinal: (historyData as any).systemsReview?.gastrointestinal || '',
        systems_review_genitourinary: (historyData as any).systemsReview?.genitourinary || '',
        systems_review_osteo_muscular: (historyData as any).systemsReview?.osteoMuscular || '',
        systems_review_neurological: (historyData as any).systemsReview?.neurological || '',
        systems_review_skin_appendages: (historyData as any).systemsReview?.skinAndAppendages || ''
      };
      // Try full insert first (with physical exam columns)
      let data: any = null;
      let insertError: any = null;
      const {
        data: fullData,
        error: fullError
      } = await supabase.from('clinical_histories').insert({
        ...baseInsert,
        ...physExamColumns
      }).select().single();
      if (fullError) {
        // If error is about missing columns, retry without physical exam columns
        const errorMsg = fullError.message || '';
        const isColumnError = errorMsg.includes('column') || errorMsg.includes('undefined') || fullError.code === '42703' ||
        // undefined_column
        fullError.code === 'PGRST204';
        if (isColumnError) {
          console.warn('⚠️ Columnas de examen físico no encontradas, guardando sin ellas. Ejecuta supabase-migration-physical-exam.sql');
          // Store physical exam data inside personal_background as fallback
          const personalBgWithPhysExam = {
            ...historyData.personalBackground,
            _physicalExam: physExam
          };
          const {
            data: fallbackData,
            error: fallbackError
          } = await supabase.from('clinical_histories').insert({
            ...baseInsert,
            personal_background: personalBgWithPhysExam
          }).select().single();
          if (fallbackError) {
            console.error('❌ Error guardando historial clínico (fallback):', fallbackError);
            throw new Error(`Error guardando historial: ${fallbackError.message}`);
          }
          data = fallbackData;
        } else {
          console.error('❌ Error guardando historial clínico:', fullError);
          throw new Error(`Error guardando historial: ${fullError.message}`);
        }
      } else {
        data = fullData;
      }
      const newHistory: ClinicalHistory = {
        id: data.id,
        patientId: data.patient_id,
        date: new Date(data.date + 'T12:00:00'),
        doctorId: data.doctor_id,
        doctorName: data.doctor_name,
        personalBackground: data.personal_background,
        clinicalBackground: data.clinical_background,
        physicalExam: {
          bloodPressure: data.blood_pressure || physExam.bloodPressure || '',
          bloodSugar: data.blood_sugar || physExam.bloodSugar || '',
          heartRate: data.heart_rate || physExam.heartRate || '',
          lungs: data.lungs || physExam.lungs || '',
          eyes: data.eyes || physExam.eyes || '',
          throat: data.throat || physExam.throat || '',
          abdomen: data.abdomen || physExam.abdomen || '',
          weight: data.weight || physExam.weight || '',
          height: data.height || physExam.height || '',
          temperature: data.temperature || physExam.temperature || '',
          positiveAspects: data.positive_aspects || (physExam as any).positiveAspects || '',
          respiratoryRate: data.respiratory_rate || (physExam as any).respiratoryRate || '',
          weightLb: data.weight_lb || (physExam as any).weightLb || ''
        } as any,
        diseaseHistory: {
          sinceWhen: data.disease_history_since_when || (historyData as any).diseaseHistory?.sinceWhen || '',
          previousTreatment: data.disease_history_previous_treatment || (historyData as any).diseaseHistory?.previousTreatment || '',
          evolution: data.disease_history_evolution || (historyData as any).diseaseHistory?.evolution || ''
        },
        systemsReview: {
          general: data.systems_review_general || (historyData as any).systemsReview?.general || '',
          cardioRespiratory: data.systems_review_cardio_respiratory || (historyData as any).systemsReview?.cardioRespiratory || '',
          gastrointestinal: data.systems_review_gastrointestinal || (historyData as any).systemsReview?.gastrointestinal || '',
          genitourinary: data.systems_review_genitourinary || (historyData as any).systemsReview?.genitourinary || '',
          osteoMuscular: data.systems_review_osteo_muscular || (historyData as any).systemsReview?.osteoMuscular || '',
          neurological: data.systems_review_neurological || (historyData as any).systemsReview?.neurological || '',
          skinAndAppendages: data.systems_review_skin_appendages || (historyData as any).systemsReview?.skinAndAppendages || ''
        },
        diagnosis: data.diagnosis,
        treatment: data.treatment,
        medicalIndications: data.medical_indications || '',
        sessions: data.sessions || '',
        sessionNumber: '',
        sessionServices: data.session_services || historyData.sessionServices || [],
        comments: data.comments || '',
        doctorSignature: data.doctor_signature || '',
        attachedImages: data.attached_images || [],
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };
      setClinicalHistories(prev => [...prev, newHistory]);
      // Log the action
      const patient = patients.find(p => p.id === historyData.patientId);
      logAction('create', 'clinicalHistory', newHistory.id, patient?.name || 'Paciente', `Historial clínico creado para "${patient?.name || 'Paciente'}" por Dr. ${historyData.doctorName}`);
      return newHistory;
    } else {
      const newHistory: ClinicalHistory = {
        ...historyData,
        id: crypto.randomUUID(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setClinicalHistories(prev => [...prev, newHistory]);
      return newHistory;
    }
  };
  const updateClinicalHistory = async (id: string, updates: Partial<ClinicalHistory>) => {
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {};
      if (updates.patientId !== undefined) supabaseUpdates.patient_id = updates.patientId;
      if (updates.date !== undefined) supabaseUpdates.date = updates.date;
      if (updates.doctorId !== undefined) supabaseUpdates.doctor_id = updates.doctorId;
      if (updates.doctorName !== undefined) supabaseUpdates.doctor_name = updates.doctorName;
      if (updates.personalBackground !== undefined) supabaseUpdates.personal_background = updates.personalBackground;
      if (updates.clinicalBackground !== undefined) supabaseUpdates.clinical_background = updates.clinicalBackground;
      if (updates.physicalExam !== undefined) {
        const pe = updates.physicalExam;
        supabaseUpdates.blood_pressure = pe.bloodPressure || '';
        supabaseUpdates.blood_sugar = pe.bloodSugar || '';
        supabaseUpdates.heart_rate = pe.heartRate || '';
        supabaseUpdates.lungs = pe.lungs || '';
        supabaseUpdates.eyes = pe.eyes || '';
        supabaseUpdates.throat = pe.throat || '';
        supabaseUpdates.abdomen = pe.abdomen || '';
        supabaseUpdates.weight = pe.weight || '';
        supabaseUpdates.height = pe.height || '';
        supabaseUpdates.temperature = pe.temperature || '';
        supabaseUpdates.positive_aspects = (pe as any).positiveAspects || '';
        supabaseUpdates.respiratory_rate = (pe as any).respiratoryRate || '';
        supabaseUpdates.weight_lb = (pe as any).weightLb || '';
      }
      if ((updates as any).diseaseHistory !== undefined) {
        const dh = (updates as any).diseaseHistory;
        supabaseUpdates.disease_history_since_when = dh?.sinceWhen || '';
        supabaseUpdates.disease_history_previous_treatment = dh?.previousTreatment || '';
        supabaseUpdates.disease_history_evolution = dh?.evolution || '';
      }
      if ((updates as any).systemsReview !== undefined) {
        const sr = (updates as any).systemsReview;
        supabaseUpdates.systems_review_general = sr?.general || '';
        supabaseUpdates.systems_review_cardio_respiratory = sr?.cardioRespiratory || '';
        supabaseUpdates.systems_review_gastrointestinal = sr?.gastrointestinal || '';
        supabaseUpdates.systems_review_genitourinary = sr?.genitourinary || '';
        supabaseUpdates.systems_review_osteo_muscular = sr?.osteoMuscular || '';
        supabaseUpdates.systems_review_neurological = sr?.neurological || '';
        supabaseUpdates.systems_review_skin_appendages = sr?.skinAndAppendages || '';
      }
      if (updates.diagnosis !== undefined) supabaseUpdates.diagnosis = updates.diagnosis;
      if (updates.treatment !== undefined) supabaseUpdates.treatment = updates.treatment;
      if (updates.medicalIndications !== undefined) supabaseUpdates.medical_indications = updates.medicalIndications;
      if (updates.sessions !== undefined) supabaseUpdates.sessions = updates.sessions;
      if (updates.sessionServices !== undefined) supabaseUpdates.session_services = updates.sessionServices;
      if (updates.comments !== undefined) supabaseUpdates.comments = updates.comments;
      if (updates.doctorSignature !== undefined) supabaseUpdates.doctor_signature = updates.doctorSignature;
      if ((updates as any).status !== undefined) supabaseUpdates.status = (updates as any).status;
      if (Object.keys(supabaseUpdates).length > 0) {
        const {
          error
        } = await supabase.from('clinical_histories').update(supabaseUpdates).eq('id', id);
        if (error) throw error;
      }
    }
    setClinicalHistories(prev => prev.map(ch => ch.id === id ? {
      ...ch,
      ...updates,
      updatedAt: new Date()
    } : ch));
  };
  const deleteClinicalHistory = async (id: string) => {
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('clinical_histories').delete().eq('id', id);
      if (error) throw error;
    }
    setClinicalHistories(prev => prev.filter(ch => ch.id !== id));
  };
  const getClinicalHistory = (id: string) => clinicalHistories.find(ch => ch.id === id);
  const getClinicalHistoriesByPatient = (patientId: string) => clinicalHistories.filter(ch => ch.patientId === patientId);
  // ==================== ROLES ====================
  const addRole = async (roleData: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>): Promise<Role> => {
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('roles').insert({
        id: crypto.randomUUID(),
        name: roleData.name,
        display_name: roleData.displayName,
        description: roleData.description,
        is_system_role: roleData.isSystemRole || false
      }).select().single();
      if (error) {
        console.error('Error creando rol en Supabase:', error);
        throw new Error(error.message);
      }
      const newRole: Role = {
        id: data.id,
        name: data.name,
        displayName: data.display_name,
        description: data.description || '',
        isSystemRole: data.is_system_role,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at)
      };
      setRoles(prev => [...prev, newRole]);
      console.log('✅ Rol creado en Supabase:', newRole.displayName);
      return newRole;
    } else {
      const newRole: Role = {
        ...roleData,
        id: crypto.randomUUID(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setRoles(prev => [...prev, newRole]);
      return newRole;
    }
  };
  const updateRole = async (id: string, updates: Partial<Role>) => {
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {};
      if (updates.displayName !== undefined) supabaseUpdates.display_name = updates.displayName;
      if (updates.description !== undefined) supabaseUpdates.description = updates.description;
      if (updates.name !== undefined) supabaseUpdates.name = updates.name;
      if ((updates as any).status !== undefined) supabaseUpdates.status = (updates as any).status;
      if (Object.keys(supabaseUpdates).length > 0) {
        const {
          error
        } = await supabase.from('roles').update(supabaseUpdates).eq('id', id);
        if (error) {
          console.error('Error actualizando rol en Supabase:', error);
          throw new Error(error.message);
        }
        console.log('✅ Rol actualizado en Supabase');
      }
    }
    setRoles(prev => prev.map(r => r.id === id ? {
      ...r,
      ...updates,
      updatedAt: new Date()
    } : r));
  };
  const deleteRole = async (id: string) => {
    if (isConnectedToSupabase) {
      // Delete permissions first (foreign key)
      const {
        error: permError
      } = await supabase.from('permissions').delete().eq('role_id', id);
      if (permError) {
        console.error('Error eliminando permisos del rol:', permError);
      }
      const {
        error
      } = await supabase.from('roles').delete().eq('id', id);
      if (error) {
        console.error('Error eliminando rol en Supabase:', error);
        throw new Error(error.message);
      }
      console.log('✅ Rol eliminado de Supabase');
    }
    // Also clean up localStorage permissions for this role
    const roleToDelete = roles.find(r => r.id === id);
    if (roleToDelete) {
      try {
        const saved = localStorage.getItem('savidna-role-permissions');
        if (saved) {
          const allPerms = JSON.parse(saved);
          Object.keys(allPerms).forEach(module => {
            delete allPerms[module][roleToDelete.name];
          });
          localStorage.setItem('savidna-role-permissions', JSON.stringify(allPerms));
        }
      } catch (e) {}
    }
    setRoles(prev => prev.filter(r => r.id !== id));
    setPermissions(prev => prev.filter(p => p.roleId !== id));
  };
  const getRole = (id: string) => roles.find(r => r.id === id);
  // ==================== PERMISSIONS ====================
  const getPermissionsByRole = (roleId: string) => permissions.filter(p => p.roleId === roleId);
  const updatePermissions = async (roleId: string, updatedPermissions: Permission[]) => {
    if (isConnectedToSupabase) {
      // OPTIMIZED: Use delete + bulk insert instead of sequential upserts (2 queries vs 54)
      try {
        // 1. Delete all existing permissions for this role
        const {
          error: deleteError
        } = await supabase.from('permissions').delete().eq('role_id', roleId);
        if (deleteError) {
          console.error('Error eliminando permisos anteriores:', deleteError);
          throw deleteError;
        }
        // 2. Bulk insert all permissions at once
        const permissionsToInsert = updatedPermissions.map(perm => ({
          id: perm.id || crypto.randomUUID(),
          role_id: roleId,
          module: perm.module,
          can_create: perm.canCreate,
          can_read: perm.canRead,
          can_update: perm.canUpdate,
          can_delete: perm.canDelete,
          permission_level: perm.permissionLevel
        }));
        const {
          error: insertError
        } = await supabase.from('permissions').insert(permissionsToInsert);
        if (insertError) {
          console.error('Error insertando permisos:', insertError);
          throw insertError;
        }
        console.log('✅ Permisos guardados en Supabase (batch)');
      } catch (err) {
        console.error('Error en batch de permisos:', err);
        throw err;
      }
      // Reload permissions from Supabase to stay in sync
      const {
        data: allPerms,
        error
      } = await supabase.from('permissions').select('*');
      if (allPerms && !error) {
        setPermissions(allPerms.map(p => ({
          id: p.id,
          roleId: p.role_id,
          module: p.module,
          canCreate: p.can_create,
          canRead: p.can_read,
          canUpdate: p.can_update,
          canDelete: p.can_delete,
          permissionLevel: p.permission_level,
          createdAt: new Date(p.created_at),
          updatedAt: new Date(p.updated_at)
        })));
        // Re-sync to savidna-role-permissions localStorage so getRolePermissions() picks up changes
        try {
          const {
            data: allRoles
          } = await supabase.from('roles').select('*');
          if (allRoles) {
            const allPermsMap: Record<string, Record<string, string>> = {};
            for (const perm of allPerms) {
              const role = allRoles.find((r: any) => r.id === perm.role_id);
              if (role) {
                if (!allPermsMap[perm.module]) {
                  allPermsMap[perm.module] = {};
                }
                // CRITICAL: Normalize role name to match AuthContext
                const normalizedName = normalizeRoleName(role.name);
                allPermsMap[perm.module][normalizedName] = perm.permission_level;
              }
            }
            localStorage.setItem('savidna-role-permissions', JSON.stringify(allPermsMap));
            console.log('✅ Permisos sincronizados a localStorage desde Supabase (nombres normalizados)');
            window.dispatchEvent(new Event('permissions-updated'));
          }
          try {} catch (e) {
            console.error('Error syncing permissions to localStorage:', e);
          }
        } finally {
          // Notify all components that permissions changed
        }
        // Notify all components that permissions changed
        window.dispatchEvent(new Event('permissions-updated'));
      }
    } else {
      // localStorage fallback
      setPermissions(prev => {
        // Remove old permissions for this role, add new ones
        const otherPerms = prev.filter(p => p.roleId !== roleId);
        const updated = [...otherPerms, ...updatedPermissions.map(p => ({
          ...p,
          updatedAt: new Date()
        }))];
        return updated;
      });
    }
  };
  // ==================== FOLLOW-UP NOTES ====================
  const addFollowUpNote = async (noteData: Omit<FollowUpNote, 'id' | 'createdAt'>): Promise<FollowUpNote> => {
    if (isConnectedToSupabase) {
      const {
        data,
        error
      } = await supabase.from('follow_up_notes').insert({
        user_id: noteData.userId,
        user_name: noteData.userName,
        contact_name: noteData.contactName,
        subject: noteData.subject,
        description: noteData.description
      }).select().single();
      if (error) throw error;
      const newNote: FollowUpNote = {
        id: data.id,
        userId: data.user_id,
        userName: data.user_name,
        contactName: data.contact_name,
        subject: data.subject,
        description: data.description,
        createdAt: new Date(data.created_at)
      };
      setFollowUpNotes(prev => [newNote, ...prev]);
      logAction('create', 'followUpNotes', newNote.id, noteData.contactName, `Nota de seguimiento para "${noteData.contactName}" - ${noteData.subject}`);
      return newNote;
    } else {
      const newNote: FollowUpNote = {
        ...noteData,
        id: crypto.randomUUID(),
        createdAt: new Date()
      };
      setFollowUpNotes(prev => [newNote, ...prev]);
      logAction('create', 'followUpNotes', newNote.id, noteData.contactName, `Nota de seguimiento para "${noteData.contactName}" - ${noteData.subject}`);
      return newNote;
    }
  };
  const updateFollowUpNote = async (id: string, updates: Partial<FollowUpNote>) => {
    const existing = followUpNotes.find(n => n.id === id);
    if (isConnectedToSupabase) {
      const supabaseUpdates: Record<string, any> = {};
      if (updates.contactName !== undefined) supabaseUpdates.contact_name = updates.contactName;
      if (updates.subject !== undefined) supabaseUpdates.subject = updates.subject;
      if (updates.description !== undefined) supabaseUpdates.description = updates.description;
      if ((updates as any).status !== undefined) supabaseUpdates.status = (updates as any).status;
      if (Object.keys(supabaseUpdates).length > 0) {
        const {
          error
        } = await supabase.from('follow_up_notes').update(supabaseUpdates).eq('id', id);
        if (error) throw error;
      }
    }
    setFollowUpNotes(prev => prev.map(n => n.id === id ? {
      ...n,
      ...updates
    } : n));
    if (existing) {
      logAction('update', 'followUpNotes', id, existing.contactName, `Nota de seguimiento para "${existing.contactName}" actualizada`);
    }
  };
  const deleteFollowUpNote = async (id: string) => {
    const existing = followUpNotes.find(n => n.id === id);
    if (isConnectedToSupabase) {
      const {
        error
      } = await supabase.from('follow_up_notes').delete().eq('id', id);
      if (error) throw error;
    }
    setFollowUpNotes(prev => prev.filter(n => n.id !== id));
    if (existing) {
      logAction('delete', 'followUpNotes', id, existing.contactName, `Nota de seguimiento para "${existing.contactName}" eliminada`);
    }
  };
  // ==================== STATS ====================
  const getTodayAppointmentsCount = () => {
    const today = getToday();
    return appointments.filter(a => a.date === today && a.status !== 'cancelada').length;
  };
  const getTotalOpportunitiesAmount = () => {
    return opportunities.reduce((sum, o) => sum + o.amount, 0);
  };
  return <DataContext.Provider value={{
    patients,
    addPatient,
    updatePatient,
    deletePatient,
    getPatient,
    updateMedicalHistory,
    addMedicalEntry,
    addPatientInteraction,
    doctors,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    getDoctor,
    getDoctorsBySpecialty,
    appointments,
    addAppointment,
    updateAppointment,
    cancelAppointment,
    rescheduleAppointment,
    completeAppointment,
    getAppointment,
    getAppointmentsByDate,
    getAppointmentsByPatient,
    getAppointmentsByDoctor,
    checkDoctorAvailability,
    syncAllAppointmentsToSheets,
    services,
    addService,
    updateService,
    deleteService,
    getService,
    insurances,
    addInsurance,
    updateInsurance,
    deleteInsurance,
    getInsurance,
    specialties,
    addSpecialty,
    updateSpecialty,
    deleteSpecialty,
    getSpecialty,
    contacts,
    addContact,
    updateContact,
    deleteContact,
    getContact,
    addContactInteraction,
    companies,
    addCompany,
    updateCompany,
    deleteCompany,
    getCompany,
    opportunities,
    addOpportunity,
    updateOpportunity,
    deleteOpportunity,
    getOpportunity,
    addOpportunityInteraction,
    clinicalHistories,
    addClinicalHistory,
    updateClinicalHistory,
    deleteClinicalHistory,
    getClinicalHistory,
    getClinicalHistoriesByPatient,
    roles,
    addRole,
    updateRole,
    deleteRole,
    getRole,
    permissions,
    getPermissionsByRole,
    updatePermissions,
    getTodayAppointmentsCount,
    getTotalOpportunitiesAmount,
    isLoading,
    isConnectedToSupabase,
    // Audit Log
    auditLogs,
    addAuditLog,
    clearAuditLogs,
    // Follow-up Notes
    followUpNotes,
    addFollowUpNote,
    updateFollowUpNote,
    deleteFollowUpNote
  }} data-id="element-4793">
      {children}
    </DataContext.Provider>;
}
export function useData(): DataContextType {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}